import {
  Avatar,
  Badge,
  Box,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  Skeleton,
  Text,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { useHasAccess } from 'lib-shared-access-control';
import { useEffect } from 'react';
import { useEffectOnce } from 'react-use';

import { rolesApi } from 'apis/roles';
import { usersApi } from 'apis/users';
import { userModalActions } from 'components/usersModal';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { getUserDisplayName } from 'utils/user';

export const SettingsAgentsRolesEditAgentsView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const hasManageMembersAccess = useHasAccess('organization.member');

  const { roleId } = useAppPathParams(
    '/settings/agents/roles/:roleType/:roleId/agents',
  );

  const {
    userIds,
    isLoading: isLoadingUserIds,
    isSuccess,
    isError,
    refetch,
  } = rolesApi.useReadRoleUsersQuery(
    { roleId },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        userIds: data?.data.user_ids ?? [],
      }),
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    items: users,
    asyncStatus,
    changeArg,
    updateItem,
    // removeItem,
  } = useLazyPaginatedEndpoint({
    endpoint: usersApi.endpoints.readUsers,
    itemsSelector: ({ data: { users } }) => users,
  });

  const isLoading = isLoadingUserIds || asyncStatus === 'pending';

  useEffect(() => {
    if (userIds.length) {
      changeArg({
        userIds: userIds.slice(0, 100),
        limit: 100,
        offset: 0,
        userType: 'member',
      });
    }
  }, [changeArg, userIds]);

  useEffectOnce(() => {
    const unsubscribe = pubSub.subscribe('userModalUserUpdated', (user) => {
      updateItem(user, (a, b) => a.id === b.id);
      refetch();
    });

    return () => unsubscribe();
  });

  const total = userIds.length;

  return (
    <>
      <Page.Body>
        <Page.Section
          title="All agents"
          titleMeta={
            !!total && (
              <Badge size="small" variant="secondary">
                {total}
              </Badge>
            )
          }
        >
          <DataTable
            columns={[
              {
                label: 'Agent',
                selector: (user) => {
                  if (isLoading) {
                    return (
                      <FlexBox alignItems="center" gap="300">
                        <Skeleton
                          width={sizes.spacer[500]}
                          height={sizes.spacer[500]}
                        />

                        <Skeleton height="14px" width="50%" />
                      </FlexBox>
                    );
                  }

                  return (
                    <FlexBox alignItems="center" gap="300">
                      <Box
                        width={sizes.spacer[500]}
                        minWidth={sizes.spacer[500]}
                      >
                        <Avatar user={user} />
                      </Box>

                      <Text.Paragraph>
                        {getUserDisplayName(user)}
                      </Text.Paragraph>
                    </FlexBox>
                  );
                },
                style: { width: '50%' },
              },
              {
                label: 'Email',
                selector: (user) => {
                  if (isLoading) {
                    return <Skeleton height="14px" width="50%" />;
                  }
                  return user.email;
                },
                style: { width: '50%' },
              },
              {
                label: '',
                selector: (user) => {
                  if (isLoading) {
                    return (
                      <FlexBox
                        justifyContent="center"
                        alignItems="center"
                        width="32px"
                        height="32px"
                      >
                        <Skeleton height="18px" width="18px" />
                      </FlexBox>
                    );
                  }

                  if (hasManageMembersAccess) {
                    return (
                      <IconButton
                        variant="tertiary"
                        icon="pen"
                        onClick={() => {
                          dispatch(userModalActions.show({ user }));
                        }}
                      />
                    );
                  }
                },
                style: {
                  width: 0,
                },
              },
            ]}
            data={
              isLoading ? (Array.from({ length: 5 }) as typeof users) : users
            }
            tableStatusProps={{
              loadingFailed: isError,
              onLoadingRetry: refetch,
              emptyState:
                !userIds.length && isSuccess
                  ? {
                      title: 'No agents have this role yet',
                      description:
                        'Assign this role to one or more agents and they will appear here.',
                      buttonProps: {
                        children: 'Go to Agents',
                        variant: 'secondary',
                        onClick: () => {
                          navigate({
                            path: '/settings/agents',
                          });
                        },
                      },
                    }
                  : undefined,
            }}
            tableStyleProps={{
              stickyHeader: true,
              stickyFooter: true,
            }}
          />
        </Page.Section>
      </Page.Body>
    </>
  );
};
